/**
 * Rejections that are noise rather than defects. Asked as one question so the Sentry
 * `beforeSend` filter and the global unhandled-rejection toast cannot drift apart —
 * an event worth hiding from one is worth hiding from the other.
 */

/**
 * True for a rejection that is only a cancelled request — an `AbortController.abort()`
 * rejecting whatever was still in flight.
 *
 * Nothing in this app aborts a request whose result still matters. Every
 * `AbortController` here supersedes a request the user has already moved past
 * (`Robots.vm.ts` and `useLiveRobotStreaming.ts`, both of which already swallow the
 * AbortError at their own call site), and MapLibre aborts a map tile's fetch the instant
 * the tile scrolls out of view (`TileManager._removeTile` -> `abortTile`). That last one
 * has no call site of ours to catch it, so it surfaced at
 * `window.onunhandledrejection` — where an ordinary map pan toasted the operator, popped
 * Sentry's crash-report dialog and filed PORTAL-WEB-X7. An abort nobody awaited is not
 * actionable by construction: whoever aborted it is the one who stopped caring about the
 * answer.
 *
 * Deliberately keyed on `name`, not on the message: "signal is aborted without reason" is
 * the browser's wording and a browser update may change it, while `AbortError` is spec.
 */
export function isAbortRejection(reason: unknown): boolean {
	return typeof reason === "object" && reason !== null && (reason as { name?: unknown }).name === "AbortError";
}
